import { auth, db, firebase, secondaryProvider } from '@/plugins/firebase/firebase';
import { isPermissionError } from '@/common/static_data/constants';

  const COLLECTION_NAME = 'users';

  export const getAdmins = async ({ page = 1, limit: limitCount = 10 } = {}) => {
    try {
      const query = db.collection(COLLECTION_NAME)
        .where('role', '==', 'admin')
        .limit(limitCount);

      const snapshot = await query.get();
      const admins = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const totalQuery = db.collection(COLLECTION_NAME)
        .where('role', '==', 'admin');
      const totalSnapshot = await totalQuery.get();
      const total = totalSnapshot.size;

      return {
        data: admins,
        total,
        page,
        hasMore: admins.length === limitCount
      };
    } catch (error) {
      if (isPermissionError(error)) {
        return {
          data: [],
          total: 0,
          page,
          hasMore: false
        };
      }

      throw new Error('Failed to fetch administrators');
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  export const createAdmin = async (adminData) => {
    try {

      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      const temporaryPassword = generateRandomPassword();

      const userCredential = await secondaryProvider.createUserWithEmailAndPassword(
        adminData.email,
        temporaryPassword
      );
      const userDoc = {
        username: adminData.name,
        email: adminData.email,
        isActive: true,
        role: 'admin',
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      };

      await db.collection(COLLECTION_NAME).doc(userCredential.user.uid).set(userDoc);

      try {
        await secondaryProvider.sendPasswordResetEmail(adminData.email);
        console.log('Password reset email sent to:', adminData.email);
      } catch (emailError) {
        console.error('Failed to send password reset email:', emailError);
      }

      await secondaryProvider.signOut();

      return {
        id: userCredential.user.uid,
        uid: userCredential.user.uid,
        passwordResetSent: true,
        ...userDoc
      };
    } catch (error) {
      console.error('Error creating admin:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Email is already registered');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password is too weak');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email format');
      }

      throw new Error('Failed to create administrator');
    }
  };

  export const updateAdmin = async (adminId, adminData) => {
    try {
      const updateData = {
        username: adminData.name,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.uid
      };

      
      await db.collection(COLLECTION_NAME).doc(adminId).update(updateData);

      return { success: true };
    } catch (error) {
      console.error('Error updating admin:', error);
      throw new Error('Failed to update administrator');
    }
  };

  export const changeAdminStatus = async (adminId, isActive) => {
    try {
      await db.collection(COLLECTION_NAME).doc(adminId).update({
        isActive: isActive,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.uid
      });

      return { success: true };
    } catch (error) {
      console.error('Error changing admin status:', error);
      throw new Error('Failed to change administrator status');
    }
  };

  export const getAdminById = async (adminId) => {
    try {
      const userDoc = await db.collection(COLLECTION_NAME).doc(adminId).get();

      if (userDoc.exists) {
        const userData = userDoc.data();
        if (userData.role !== 'admin') {
          throw new Error('User is not an administrator');
        }

        return {
          id: userDoc.id,
          ...userData
        };
      } else {
        throw new Error('Administrator not found');
      }
    } catch (error) {
      console.error('Error fetching admin by ID:', error);
      throw new Error('Failed to fetch administrator');
    }
  };
